import type {
  AIActionKey,
  LimitBehavior,
  MeterSubjectType,
  SubscriptionCatalog,
  SubscriptionSnapshot,
} from '../../../types/src/subscription';
import { subscriptionCatalog } from './catalog';

export interface MeteringResolution {
  actionKey: AIActionKey;
  countsTowardDaily: boolean;
  meterSubject: MeterSubjectType;
  meterSubjectId: string | null;
  estimatedUnits: number;
  limit: number | null;
  remaining: number | null;
  dailyLimit: number | null;
  dailyRemaining: number | null;
  cycleLimit: number | null;
  cycleRemaining: number | null;
  limitBehavior: LimitBehavior;
}

function toRemaining(
  limit: number | null,
  used: number,
  reserved: number,
  actionPackBalance: number,
) {
  if (limit === null) {
    return null;
  }

  return Math.max(limit + actionPackBalance - used - reserved, 0);
}

export function resolveMetering(
  snapshot: SubscriptionSnapshot,
  actionKey: AIActionKey,
  catalog: SubscriptionCatalog = subscriptionCatalog,
): MeteringResolution {
  const action = catalog.aiActions[actionKey];

  if (!action || !action.countsTowardDaily || action.planSet === 'onboarding') {
    return {
      actionKey,
      countsTowardDaily: false,
      meterSubject: 'none',
      meterSubjectId: null,
      estimatedUnits: 0,
      limit: null,
      remaining: null,
      dailyLimit: null,
      dailyRemaining: null,
      cycleLimit: null,
      cycleRemaining: null,
      limitBehavior: 'none',
    };
  }

  const estimatedUnits = Math.max(action.estimatedCredits ?? 1, 1);

  if (snapshot.planSet === 'individual') {
    const tierConfig = catalog.planSets.individual.tiers[snapshot.tier];
    const dailyLimit =
      tierConfig.limits.aiCreditsPerDay ?? tierConfig.limits.aiActionsPerDay;
    const cycleLimit = tierConfig.limits.aiCreditsPerCycle ?? null;
    const dailyRemaining =
      snapshot.usage.credits?.dailyRemaining ??
      toRemaining(
        dailyLimit,
        snapshot.usage.used,
        snapshot.usage.reserved,
        snapshot.usage.actionPackBalance,
      );
    const cycleRemaining =
      snapshot.usage.credits?.cycleRemaining ??
      (cycleLimit === null
        ? null
        : Math.max(
            cycleLimit -
              (snapshot.usage.credits?.cycleUsed ?? 0) -
              (snapshot.usage.credits?.cycleReserved ?? 0),
            0,
          ));
    const remainingCandidates = [dailyRemaining, cycleRemaining].filter(
      (value): value is number => value !== null,
    );
    return {
      actionKey,
      countsTowardDaily: true,
      meterSubject: 'individual_user',
      meterSubjectId: snapshot.usage.meterSubjectId,
      estimatedUnits,
      limit: dailyLimit,
      remaining:
        remainingCandidates.length > 0
          ? Math.min(...remainingCandidates)
          : null,
      dailyLimit,
      dailyRemaining,
      cycleLimit,
      cycleRemaining,
      limitBehavior: tierConfig.limitBehavior,
    };
  }

  const seatTier = snapshot.seat?.tier ?? null;
  if (seatTier) {
    const tierConfig = catalog.planSets.employer.tiers[seatTier];
    return {
      actionKey,
      countsTowardDaily: true,
      meterSubject: 'employer_seat',
      meterSubjectId:
        snapshot.usage.meterSubject === 'employer_seat'
          ? snapshot.usage.meterSubjectId
          : `${snapshot.seat?.companyId ?? 'company'}:${snapshot.seat?.userId ?? 'user'}`,
      limit: tierConfig.limits.aiActionsPerDay,
      estimatedUnits,
      remaining: toRemaining(
        tierConfig.limits.aiActionsPerDay,
        snapshot.usage.used,
        snapshot.usage.reserved,
        snapshot.usage.actionPackBalance,
      ),
      dailyLimit: tierConfig.limits.aiActionsPerDay,
      dailyRemaining: toRemaining(
        tierConfig.limits.aiActionsPerDay,
        snapshot.usage.used,
        snapshot.usage.reserved,
        snapshot.usage.actionPackBalance,
      ),
      cycleLimit: null,
      cycleRemaining: null,
      limitBehavior: tierConfig.limitBehavior,
    };
  }

  const workspaceFreePool = catalog.planSets.employer.workspaceFreePool;
  if (
    workspaceFreePool &&
    snapshot.workspace?.workspaceTier === 'free' &&
    action.availableToEmployerFreeWorkspace
  ) {
    return {
      actionKey,
      countsTowardDaily: true,
      meterSubject: 'employer_workspace',
      meterSubjectId:
        snapshot.usage.meterSubject === 'employer_workspace'
          ? snapshot.usage.meterSubjectId
          : snapshot.workspace.companyId,
      limit: workspaceFreePool.aiActionsPerDay,
      estimatedUnits,
      remaining: toRemaining(
        workspaceFreePool.aiActionsPerDay,
        snapshot.usage.used,
        snapshot.usage.reserved,
        snapshot.usage.actionPackBalance,
      ),
      dailyLimit: workspaceFreePool.aiActionsPerDay,
      dailyRemaining: toRemaining(
        workspaceFreePool.aiActionsPerDay,
        snapshot.usage.used,
        snapshot.usage.reserved,
        snapshot.usage.actionPackBalance,
      ),
      cycleLimit: null,
      cycleRemaining: null,
      limitBehavior: catalog.planSets.employer.tiers.free.limitBehavior,
    };
  }

  return {
    actionKey,
    countsTowardDaily: true,
    meterSubject: 'none',
    meterSubjectId: null,
    estimatedUnits,
    limit: 0,
    remaining: 0,
    dailyLimit: 0,
    dailyRemaining: 0,
    cycleLimit: null,
    cycleRemaining: null,
    limitBehavior: 'hard_stop',
  };
}

export function getRemainingActionsToday(
  snapshot: SubscriptionSnapshot,
  actionKey: AIActionKey,
  catalog: SubscriptionCatalog = subscriptionCatalog,
) {
  return resolveMetering(snapshot, actionKey, catalog).remaining;
}
